import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const {
    user,
    loading,
    profileLoading,
    profileReady,
    profileError,
    session,
    organizationId,
    refreshUserData,
  } = useAuth();
  const location = useLocation();
  const waitingForProfile =
    !!user &&
    !!session &&
    !profileError &&
    !profileReady &&
    !organizationId &&
    location.pathname !== '/setup';

  useEffect(() => {
    console.log('ProtectedRoute check - Auth status:', {
      isAuthenticated: !!user && !!session,
      isLoading: loading,
      profileLoading,
      profileReady,
      profileError,
      path: location.pathname,
      userId: user?.id,
      organizationId,
    });
  }, [
    user,
    session,
    loading,
    profileLoading,
    profileReady,
    profileError,
    organizationId,
    location.pathname,
  ]);

  if (loading || waitingForProfile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-lg font-medium">Trwa ładowanie...</p>
        </div>
      </div>
    );
  }

  if (!user || !session) {
    // Redirect to login if not authenticated
    console.log('User not authenticated in ProtectedRoute, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (profileError && !organizationId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-center shadow-sm">
          <p className="text-lg font-medium text-foreground">Nie udało się pobrać profilu</p>
          <p className="text-sm text-muted-foreground">{profileError}</p>
          <button
            type="button"
            onClick={refreshUserData}
            disabled={profileLoading}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    );
  }

  if (!organizationId && profileReady && location.pathname !== '/setup') {
    console.log('Authenticated user has no organization loaded, redirecting to setup');
    return <Navigate to="/setup" replace />;
  }

  // User is authenticated, show children
  return <>{children}</>;
};

export default ProtectedRoute;
